import * as React from 'react';

interface IUseCountdownReturn {
  secondsRemaining: number;
  isActive: boolean;
  start: (durationSeconds: number) => void;
  reset: () => void;
}

/**
 * Countdown timer hook for OTP resend delays and similar timed UI states.
 *
 * @returns `secondsRemaining` (current count), `isActive` (whether counting),
 *   `start(seconds)` to begin/restart, and `reset()` to stop and zero out.
 */
const useCountdown = (): IUseCountdownReturn => {
  const [secondsRemaining, setSecondsRemaining] = React.useState(0);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = React.useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = React.useCallback(
    (durationSeconds: number) => {
      clearTimer();
      setSecondsRemaining(durationSeconds);
      intervalRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            clearTimer();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clearTimer]
  );

  const reset = React.useCallback(() => {
    clearTimer();
    setSecondsRemaining(0);
  }, [clearTimer]);

  React.useEffect(() => clearTimer, [clearTimer]);

  return {
    secondsRemaining,
    isActive: secondsRemaining > 0,
    start,
    reset,
  };
};

export default useCountdown;
